import { Link } from "react-router-dom";
import type { AccountRequestStatus, AccountRequestTracking } from "@/types";
import { formatDate } from "@/utils/format";
import { CONTACT } from "@/config/contact";

interface RequestStatusCardProps {
  request: AccountRequestTracking;
  onNewSearch: () => void;
}

interface StatusMeta {
  label: string;
  icon: string;
  chip: string;
  panel: string;
  panelIcon: string;
  panelTitle: string;
  panelText: string;
}

const statusMeta: Record<AccountRequestStatus, StatusMeta> = {
  PENDING: {
    label: "En attente de validation",
    icon: "ri-time-line",
    chip: "bg-accent-100 text-accent-800",
    panel: "border-accent-200 bg-accent-50",
    panelIcon: "text-accent-600",
    panelTitle: "Votre demande est en cours d'examen",
    panelText:
      "L'administrateur de la plateforme vérifie les informations transmises. Revenez sur cette page avec votre référence pour connaître la décision.",
  },
  APPROVED: {
    label: "Demande validée",
    icon: "ri-checkbox-circle-fill",
    chip: "bg-primary-100 text-primary-800",
    panel: "border-primary-200 bg-primary-50",
    panelIcon: "text-primary-700",
    panelTitle: "Félicitations, votre école est ouverte",
    panelText:
      "L'administrateur de la plateforme vous transmet vos identifiants. Le mot de passe provisoire vous sera demandé à la première connexion, puis vous choisirez le vôtre.",
  },
  REJECTED: {
    label: "Demande refusée",
    icon: "ri-close-circle-line",
    chip: "bg-secondary-200 text-secondary-800",
    panel: "border-secondary-200 bg-secondary-50",
    panelIcon: "text-secondary-700",
    panelTitle: "Cette demande n'a pas été retenue",
    panelText:
      `Vous pouvez déposer une nouvelle demande avec des informations à jour, ou écrire à ${CONTACT.email}.`,
  },
};

/** Étapes du parcours affichées sous forme de frise chronologique. */
function stepsFor(request: AccountRequestTracking) {
  const reviewed = request.status !== "PENDING";
  const refused = request.status === "REJECTED";
  return [
    { title: "Demande reçue", detail: `Déposée le ${formatDate(request.createdAt)}`, state: "done" },
    {
      title: "Examen de la plateforme",
      detail: reviewed ? "Traitement effectué" : "En cours d'analyse",
      state: reviewed ? "done" : "current",
    },
    {
      title: refused ? "Décision : refusée" : "Décision : validée",
      detail: request.reviewedAt ? `Le ${formatDate(request.reviewedAt)}` : "En attente de décision",
      state: request.status === "APPROVED" ? "done" : refused ? "refused" : "todo",
    },
  ];
}

const stepStyle: Record<string, { ring: string; inner: string; icon: string }> = {
  done: { ring: "border-primary-300 bg-primary-50", inner: "bg-primary-600", icon: "ri-check-line text-background-50" },
  current: { ring: "border-accent-300 bg-accent-50", inner: "bg-accent-500", icon: "ri-more-line text-accent-950" },
  refused: { ring: "border-secondary-300 bg-secondary-100", inner: "bg-secondary-500", icon: "ri-close-line text-background-50" },
  todo: { ring: "border-background-200 bg-background-100", inner: "bg-background-300", icon: "ri-more-line text-foreground-600" },
};

/** État d'une demande vu par le demandeur (aucun détail personnel n'est renvoyé par le serveur). */
export default function RequestStatusCard({ request, onNewSearch }: RequestStatusCardProps) {
  const meta = statusMeta[request.status];
  const steps = stepsFor(request);

  return (
    <div className="animate-scale-in overflow-hidden rounded-xl border border-background-200 bg-background-50">
      <div className="flex flex-col gap-4 border-b border-background-200 bg-background-100/60 px-5 py-5 sm:flex-row sm:items-start sm:justify-between md:px-7">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-foreground-400">Référence</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-semibold text-foreground-950">{request.reference}</span>
            <span
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.chip}`}
            >
              <i className={meta.icon}></i>
              {meta.label}
            </span>
          </div>
          <h2 className="mt-3 text-lg font-semibold font-heading text-foreground-950 md:text-xl">
            {request.schoolName}
          </h2>
        </div>
        <button
          onClick={onNewSearch}
          className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
        >
          <i className="ri-search-line"></i>
          Nouvelle recherche
        </button>
      </div>

      <div className="px-5 py-6 md:px-7">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          {steps.map((step) => {
            const s = stepStyle[step.state] ?? stepStyle.todo;
            return (
              <div key={step.title} className="flex items-start gap-3">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${s.ring}`}>
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full ${s.inner}`}>
                    <i className={`${s.icon} text-sm`}></i>
                  </span>
                </span>
                <div className="min-w-0 pt-0.5">
                  <p className="text-sm font-medium text-foreground-900">{step.title}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{step.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`m-5 flex items-start gap-3 rounded-lg border px-4 py-3.5 md:m-7 ${meta.panel}`}>
        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center ${meta.panelIcon}`}>
          <i className="ri-information-line text-lg"></i>
        </span>
        <div>
          <p className="text-sm font-medium text-foreground-900">{meta.panelTitle}</p>
          {request.status === "REJECTED" && request.rejectionReason && (
            <p className="mt-1 text-xs text-foreground-800">
              <strong className="font-medium">Motif :</strong> {request.rejectionReason}
            </p>
          )}
          <p className="mt-1 text-xs leading-relaxed text-foreground-600">{meta.panelText}</p>
          {request.status === "APPROVED" && (
            <Link
              to="/login"
              className="mt-3 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
            >
              <i className="ri-login-box-line"></i>
              Se connecter
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
